'use client';

import getCoupons from '@/actions/byMember/getCoupons';
import Coupons from '@/models/Coupons';
import { createGlobalStore, GlobalStore } from '@/stores/global-store';
import { createContext, ReactNode, useContext, useRef } from 'react';
import { StoreApi, useStore } from 'zustand';

interface GlobalContextProviderProps {
  children: ReactNode;
  member?: any; // This could be typed more specifically if member type is available
  [key: string]: any; // For other props passed through
}

interface GlobalContextValue {
  store: StoreApi<GlobalStore>;
  getCouponsList: (page?: number, status?: 'all' | 'active' | 'expired') => Promise<any>;
  getLuggageGoCouponsList: (page?: number, status?: 'all' | 'active' | 'expired') => Promise<any>;
  updateCoupons: (page?: number, status?: 'all' | 'active' | 'expired') => Promise<any>;
  member?: any;
  [key: string]: any;
}

export const GlobalContext = createContext<GlobalContextValue | null>(null);

export const GlobalContextProvider = ({ children, ...props }: GlobalContextProviderProps) => {
  const storeRef = useRef<StoreApi<GlobalStore>>();

  if (!storeRef.current) {
    storeRef.current = createGlobalStore();
  }

  const { coupons, setCoupons } = useStore(storeRef.current);
  const loadingRef = useRef(false);

  const getCouponsList = async (page: number = 1, status: 'all' | 'active' | 'expired' = 'all') => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    try {
      const res = await getCoupons({
        page,
        per_page: 10,
        status,
      });

      if ('data' in res) {
        setCoupons(new Coupons(res.data as any));
      }

      return res;
    } catch (error) {
      console.error(error);
    } finally {
      loadingRef.current = false;
    }
  };

  const getLuggageGoCouponsList = async (
    page: number = 1,
    status: 'all' | 'active' | 'expired' = 'all',
  ) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    try {
      const res = await getCoupons({
        page,
        per_page: 10,
        status,
        coupon_type: 'ucare',
      });

      if ('data' in res) {
        setCoupons(new Coupons(res.data as any));
      }

      return res;
    } catch (error) {
      console.error(error);
    } finally {
      loadingRef.current = false;
    }
  };

  const updateCoupons = async (page: number = 1, status: 'all' | 'active' | 'expired' = 'all') => {
    try {
      const res = await getCoupons({
        page,
        per_page: 10,
        status,
      });

      if ('data' in res && coupons) {
        coupons.insertCoupons(res.data as any);
        setCoupons(coupons);
      }

      return res;
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <GlobalContext.Provider
      value={{
        store: storeRef.current,
        getCouponsList,
        updateCoupons,
        getLuggageGoCouponsList,
        ...props,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalStore = <T,>(selector?: (store: GlobalStore) => T): T => {
  const globalContext = useContext(GlobalContext);

  if (!globalContext) {
    throw new Error('useGlobalStore must be used within GlobalStoreProvider');
  }

  // @ts-expect-error bad type conflict with react hook
  return useStore(globalContext.store, selector);
};

export const useGlobalContext = () => {
  const globalContext = useContext(GlobalContext);

  if (!globalContext) {
    throw new Error('useGlobalContext must be used within GlobalStoreProvider');
  }

  const { store, ...contextWithoutStore } = globalContext;

  return contextWithoutStore;
};
